import React, { useEffect, useState, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Select, Button, message, Result } from 'antd';
import { CrownOutlined, ReloadOutlined, EyeOutlined, EyeInvisibleOutlined, GlobalOutlined } from '@ant-design/icons';
import '../admin.css';
import { hasFeatureAccess, getUpgradeMessage } from '../utils/planRestrictions';
import UpgradeModal from '../components/UpgradeModal';
import { API_BASE, authHeaders } from '../../config/api';

// ─── DESIGN TOKENS ────────────────────────────────────────────────────────────
const T = {
  bg:        '#ffffff',
  bgPanel:   '#f8fafc',
  bgCard:    '#ffffff',
  bgHover:   '#f1f5f9',
  border:    '#e2e8f0',
  borderHi:  '#cbd5e1',
  teal:      '#0ea5e9',
  tealD:     '#0284c7',
  tealGlow:  'rgba(14,165,233,0.15)',
  cyan:      '#06b6d4',
  green:     '#10b981',
  greenD:    '#059669',
  amber:     '#f59e0b',
  rose:      '#ef4444',
  purple:    '#8b5cf6',
  txtPri:    '#1e293b',
  txtSec:    '#64748b',
  txtMuted:  '#94a3b8',
  mono:      "'JetBrains Mono', 'Fira Code', 'Cascadia Code', monospace",
  sans:      "'DM Sans', system-ui, sans-serif",
};

// ─── HEATMAP RENDERER ─────────────────────────────────────────────────────────
function renderHeatmap(canvas, points, opts = {}) {
  if (!canvas || !points.length) return;
  const { radius = 28, maxOpacity = 0.85, minOpacity = 0, blur = 0.85 } = opts;
  const W = canvas.width, H = canvas.height;
  if (!W || !H) return;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, W, H);

  const offscreen = document.createElement('canvas');
  offscreen.width = W; offscreen.height = H;
  const octx = offscreen.getContext('2d');
  const maxVal = points.reduce((m, p) => Math.max(m, p.value || 1), 1);

  for (const pt of points) {
    const r = pt.radius || radius;
    const alpha = Math.min(1, (pt.value || 1) / maxVal);
    const grad = octx.createRadialGradient(pt.x, pt.y, 0, pt.x, pt.y, r);
    grad.addColorStop(0, `rgba(0,0,0,${alpha})`);
    grad.addColorStop(blur, `rgba(0,0,0,${alpha * 0.4})`);
    grad.addColorStop(1, 'rgba(0,0,0,0)');
    octx.fillStyle = grad;
    octx.fillRect(pt.x - r, pt.y - r, r * 2, r * 2);
  }

  const imgData = octx.getImageData(0, 0, W, H);
  const data = imgData.data;
  const rampCanvas = document.createElement('canvas');
  rampCanvas.width = 256; rampCanvas.height = 1;
  const rctx = rampCanvas.getContext('2d');
  const grad = rctx.createLinearGradient(0, 0, 256, 0);
  grad.addColorStop(0,    'rgba(0,0,255,0)');
  grad.addColorStop(0.20, 'rgba(0,0,255,1)');
  grad.addColorStop(0.40, 'rgba(0,255,255,1)');
  grad.addColorStop(0.60, 'rgba(0,255,0,1)');
  grad.addColorStop(0.80, 'rgba(255,255,0,1)');
  grad.addColorStop(0.92, 'rgba(255,128,0,1)');
  grad.addColorStop(1.0,  'rgba(255,0,0,1)');
  rctx.fillStyle = grad;
  rctx.fillRect(0, 0, 256, 1);
  const palette = rctx.getImageData(0, 0, 256, 1).data;

  const outData = ctx.createImageData(W, H);
  const out = outData.data;
  for (let i = 0; i < data.length; i += 4) {
    const intensity = data[i + 3];
    if (intensity === 0) continue;
    const mapped = Math.min(255, Math.floor(intensity));
    const pi = mapped * 4;
    const opacity = minOpacity + (maxOpacity - minOpacity) * (intensity / 255);
    out[i] = palette[pi]; out[i+1] = palette[pi+1]; out[i+2] = palette[pi+2];
    out[i+3] = Math.round(opacity * 255);
  }
  ctx.putImageData(outData, 0, 0);
}

function clusterEvents(events, bucketSize = 24) {
  const buckets = new Map();
  for (const ev of events) {
    const col = Math.round((ev.x || 0) / bucketSize);
    const row = Math.round((ev.y || 0) / bucketSize);
    const key = `${col}:${row}`;
    if (!buckets.has(key)) buckets.set(key, { xSum: 0, ySum: 0, count: 0 });
    const b = buckets.get(key);
    b.xSum += ev.x || 0; b.ySum += ev.y || 0; b.count += 1;
  }
  return Array.from(buckets.values()).map(b => ({ x: b.xSum / b.count, y: b.ySum / b.count, value: b.count }));
}

// ─── SMALL COMPONENTS ────────────────────────────────────────────────────────
const StatPill = ({ color, label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px', background: `${color}10`, border: `1px solid ${color}30`, borderRadius: 99, fontFamily: T.mono, fontSize: 11 }}>
    <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, boxShadow: `0 0 6px ${color}` }} />
    <span style={{ color: T.txtSec, letterSpacing: '0.04em' }}>{label}</span>
    <span style={{ color, fontWeight: 700 }}>{value}</span>
  </div>
);

const ModeBtn = ({ active, icon, label, value, onChange }) => (
  <button onClick={() => onChange(value)} style={{
    display: 'flex', alignItems: 'center', gap: 7, padding: '7px 14px',
    background: active ? `${T.teal}18` : 'transparent',
    border: `1px solid ${active ? T.teal : T.border}`,
    borderRadius: 8, cursor: 'pointer', transition: 'all 0.18s',
    color: active ? T.teal : T.txtSec, fontSize: 12, fontFamily: T.sans,
    boxShadow: active ? `0 0 12px ${T.tealGlow}` : 'none',
  }}
    onMouseEnter={e => { if (!active) { e.currentTarget.style.borderColor = T.teal + '60'; e.currentTarget.style.color = T.txtPri; } }}
    onMouseLeave={e => { if (!active) { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.txtSec; } }}
  >
    <span style={{ fontSize: 14 }}>{icon}</span>
    <span style={{ fontWeight: active ? 600 : 400 }}>{label}</span>
  </button>
);

const PageItem = ({ page, active, onClick, getColor }) => (
  <div onClick={onClick} style={{
    padding: '10px 12px', margin: '3px 0', borderRadius: 8, cursor: 'pointer',
    background: active ? `${T.teal}14` : 'transparent',
    border: `1px solid ${active ? T.teal + '50' : 'transparent'}`,
    transition: 'all 0.15s',
  }}
    onMouseEnter={e => { if (!active) { e.currentTarget.style.background = T.bgHover; e.currentTarget.style.borderColor = T.border; } }}
    onMouseLeave={e => { if (!active) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = 'transparent'; } }}
  >
    <div style={{ fontWeight: 500, fontSize: 12, color: active ? T.teal : T.txtPri, marginBottom: 3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      {page.title}
    </div>
    <div style={{ fontFamily: T.mono, color: T.txtMuted, fontSize: 10, marginBottom: 5 }}>{page.path}</div>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 4, background: `${getColor(page.type)}20`, color: getColor(page.type), fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
        {page.type}
      </span>
      <span style={{ fontSize: 9, padding: '2px 7px', borderRadius: 4, background: page.status === 'active' ? `${T.green}20` : `${T.txtMuted}20`, color: page.status === 'active' ? T.green : T.txtMuted, fontWeight: 600 }}>
        {page.status}
      </span>
    </div>
  </div>
);

// ─── WEBSITE PREVIEW ─────────────────────────────────────────────────────────
const WebsitePreview = forwardRef(({ onStatusChange }, ref) => {
  const [previewUrl, setPreviewUrl] = useState('');
  const [loading, setLoading] = useState(false);
  const [sitePages, setSitePages] = useState([]);
  const [showPagesList, setShowPagesList] = useState(true);
  const [trackedWebsites, setTrackedWebsites] = useState([]);
  const [selectedWebsite, setSelectedWebsite] = useState(null);
  const [realTimeEvents, setRealTimeEvents] = useState([]);
  const [loadingRealData, setLoadingRealData] = useState(false);
  const [hasClicked, setHasClicked] = useState(false);
  const [heatmapStyle, setHeatmapStyle] = useState('click');
  const [scrollDepth, setScrollDepth] = useState(0);
  const [rageClicks, setRageClicks] = useState([]);
  const [iframeScrollY, setIframeScrollY] = useState(0);
  const [iframeHeight, setIframeHeight] = useState(2000);
  const iframeHeightRef = useRef(2000);
  const liveEventsRef = useRef([]);
  const [liveEventsTick, setLiveEventsTick] = useState(0);

  const canvasRef    = useRef(null);
  const containerRef = useRef(null);
  const iframeRef    = useRef(null);
  const latestReqRef = useRef(0);
  const hotspotsRef  = useRef([]);
  const demoTimerRef = useRef(null);
  const iframeScrollRef = useRef({ x: 0, y: 0, viewportW: 0, viewportH: 0 });
  const accScrollYRef   = useRef(0);
  const overlayRef      = useRef(null);

  const reseedDots = useCallback((scrollY, vpW, vpH) => {
    const freshEvents = [];
    const jitter = r => (Math.random() - 0.5) * r;
    const totalH = iframeHeightRef.current || 3000;
    const hotspots = hotspotsRef.current;
    if (hotspots.length > 0) {
      hotspots.forEach(pt => {
        const count = 3 + Math.floor(Math.random() * 8);
        for (let i = 0; i < count; i++) freshEvents.push({ type: 'click', x: pt.x + jitter(40), y: pt.y + jitter(24), ts: Date.now() - Math.random() * 30000 });
      });
    } else {
      [{ rx:0.50,ry:0.04 },{ rx:0.72,ry:0.04 },{ rx:0.28,ry:0.04 },{ rx:0.85,ry:0.04 },{ rx:0.15,ry:0.04 },{ rx:0.50,ry:0.14 },{ rx:0.32,ry:0.16 },{ rx:0.25,ry:0.28 },{ rx:0.70,ry:0.28 },{ rx:0.50,ry:0.42 },{ rx:0.38,ry:0.44 },{ rx:0.62,ry:0.44 },{ rx:0.20,ry:0.55 },{ rx:0.75,ry:0.57 },{ rx:0.50,ry:0.68 },{ rx:0.30,ry:0.70 },{ rx:0.70,ry:0.70 },{ rx:0.45,ry:0.82 },{ rx:0.55,ry:0.82 },{ rx:0.50,ry:0.93 },{ rx:0.25,ry:0.95 },{ rx:0.75,ry:0.95 }]
        .forEach(({ rx, ry }) => {
          const count = 3 + Math.floor(Math.random() * 7);
          for (let i = 0; i < count; i++) freshEvents.push({ type: 'click', x: rx * vpW + jitter(55), y: ry * totalH + jitter(35), ts: Date.now() - Math.random() * 30000 });
        });
    }
    liveEventsRef.current = freshEvents;
    setIframeScrollY(0); setLiveEventsTick(t => t + 1);
  }, []);

  const apiBase = API_BASE;

  const docToCanvas = useCallback((docX, docY) => {
    const canvas = canvasRef.current, container = containerRef.current;
    if (!canvas || !container) return { x: docX, y: docY };
    const cW = canvas.width || container.clientWidth || 800;
    const cH = canvas.height || container.clientHeight || 500;
    const scroll = iframeScrollRef.current;
    const sx = scroll.x || 0, sy = scroll.y || 0;
    const vpW = scroll.viewportW > 0 ? scroll.viewportW : cW;
    const vpH = scroll.viewportH > 0 ? scroll.viewportH : cH;
    return { x: ((docX - sx) / vpW) * cW, y: ((docY - sy) / vpH) * cH };
  }, []);

  const viewportToDoc = useCallback((vpX, vpY) => {
    const { x: scrollX, y: scrollY } = iframeScrollRef.current;
    return { x: vpX + scrollX, y: vpY + scrollY };
  }, []);

  const snapToHotspot = useCallback((docX, docY) => {
    const hotspots = hotspotsRef.current;
    if (!hotspots.length) return { x: docX, y: docY };
    let best = null, bestDist = Infinity;
    for (const pt of hotspots) { const d = Math.hypot(pt.x - docX, pt.y - docY); if (d < bestDist) { bestDist = d; best = pt; } }
    return best && bestDist <= 80 ? { x: best.x, y: best.y } : { x: docX, y: docY };
  }, []);

  const syncCanvasSize = useCallback(() => {
    const canvas = canvasRef.current, container = containerRef.current;
    if (!canvas || !container) return;
    const w = container.clientWidth || 1, h = container.clientHeight || 1;
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
  }, []);

  const collectInteractiveHotspots = useCallback(() => {
    const iframe = iframeRef.current, container = containerRef.current;
    if (!iframe || !container) { hotspotsRef.current = []; return; }
    try {
      const iWin = iframe.contentWindow, doc = iWin?.document;
      if (!doc) throw new Error();
      const els = doc.querySelectorAll('a[href], button, [role="button"], input[type="button"], input[type="submit"], .btn, .button, [class*="btn"], [class*="cta"], [class*="card"], nav li, nav a, header a');
      const getDocOffset = el => { let x = 0, y = 0, node = el; while (node) { x += node.offsetLeft || 0; y += node.offsetTop || 0; node = node.offsetParent; } return { x, y }; };
      hotspotsRef.current = Array.from(els).filter(el => { const r = el.getBoundingClientRect(); return r.width > 0 && r.height > 0; }).map(el => { const r = el.getBoundingClientRect(), off = getDocOffset(el); return { x: off.x + r.width / 2, y: off.y + r.height / 2 }; }).filter(p => Number.isFinite(p.x) && Number.isFinite(p.y) && p.x > 0 && p.y > 0);
    } catch { hotspotsRef.current = []; }
  }, []);

  const applyIframeScroll = useCallback((next) => {
    const prev = iframeScrollRef.current;
    if (prev.x === next.x && prev.y === next.y && prev.viewportW === next.viewportW && prev.viewportH === next.viewportH) return;
    iframeScrollRef.current = next;
    const scrollY = next.y || 0, vpW = next.viewportW || 800, vpH = next.viewportH || 500;
    const freshEvents = [];
    [{ rx:0.50,ry:0.15 },{ rx:0.20,ry:0.30 },{ rx:0.75,ry:0.25 },{ rx:0.40,ry:0.55 },{ rx:0.65,ry:0.70 },{ rx:0.85,ry:0.45 },{ rx:0.30,ry:0.80 }].forEach(({ rx, ry }) => {
      const count = 3 + Math.floor(Math.random() * 6);
      for (let i = 0; i < count; i++) freshEvents.push({ type: 'click', x: scrollY * 0 + rx * vpW + (Math.random() - 0.5) * 50, y: scrollY + ry * vpH + (Math.random() - 0.5) * 35, ts: Date.now() });
    });
    liveEventsRef.current = freshEvents;
    setIframeScrollY(scrollY); setLiveEventsTick(t => t + 1);
  }, []);

  useEffect(() => {
    const handler = (event) => {
      try {
        const d = event.data;
        if (!d || d.__asuma !== true || d.type !== 'scroll') return;
        const scrollY = Number(d.scrollY) || 0, vpW = Number(d.viewportWidth) || 800, vpH = Number(d.viewportHeight) || 500;
        accScrollYRef.current = scrollY;
        applyIframeScroll({ x: Number(d.scrollX) || 0, y: scrollY, viewportW: vpW, viewportH: vpH });
        reseedDots(scrollY, vpW, vpH);
      } catch { }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [applyIframeScroll, reseedDots]);

  useEffect(() => {
    if (!previewUrl) return;
    let rafId;
    const poll = () => {
      const iframe = iframeRef.current, container = containerRef.current;
      const fallbackW = iframe?.clientWidth || container?.clientWidth || 800, fallbackH = iframe?.clientHeight || container?.clientHeight || 500;
      try {
        const win = iframe?.contentWindow, doc = win?.document;
        if (win && doc) { applyIframeScroll({ x: win.scrollX ?? doc.documentElement?.scrollLeft ?? 0, y: win.scrollY ?? doc.documentElement?.scrollTop ?? 0, viewportW: win.innerWidth || fallbackW, viewportH: win.innerHeight || fallbackH }); }
        else { const prev = iframeScrollRef.current; if (!prev.viewportW || !prev.viewportH) applyIframeScroll({ ...prev, viewportW: fallbackW, viewportH: fallbackH }); }
      } catch { const prev = iframeScrollRef.current; if (!prev.viewportW || !prev.viewportH) applyIframeScroll({ ...prev, viewportW: fallbackW, viewportH: fallbackH }); }
      rafId = requestAnimationFrame(poll);
    };
    rafId = requestAnimationFrame(poll);
    return () => cancelAnimationFrame(rafId);
  }, [previewUrl, applyIframeScroll]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !previewUrl) return;
    const addEvent = (ev) => { liveEventsRef.current = [...liveEventsRef.current.slice(-300), ev]; setLiveEventsTick(t => t + 1); };
    const handleClick = (e) => {
      const rect = container.getBoundingClientRect(), vpX = e.clientX - rect.left, vpY = e.clientY - rect.top;
      const doc = snapToHotspot(...Object.values(viewportToDoc(vpX, vpY)));
      const now = Date.now();
      setHasClicked(true);
      addEvent({ type: 'click', x: doc.x, y: doc.y, ts: now });
      const recent = liveEventsRef.current.filter(ev => ev.type === 'click' && now - ev.ts < 500 && Math.hypot(ev.x - doc.x, ev.y - doc.y) < 50);
      if (recent.length >= 3) setRageClicks(prev => [...prev.slice(-20), { x: doc.x, y: doc.y, count: recent.length, ts: now }]);
    };
    let moveTimer = null;
    const handleMouseMove = (e) => {
      if (moveTimer) return;
      moveTimer = setTimeout(() => { const rect = container.getBoundingClientRect(); addEvent({ type: 'mousemove', x: e.clientX - rect.left + iframeScrollRef.current.x, y: e.clientY - rect.top + iframeScrollRef.current.y, ts: Date.now() }); moveTimer = null; }, 50);
    };
    const handleScroll = (e) => { const scrollTop = e.target.scrollTop || container.scrollTop, scrollHeight = container.scrollHeight - container.clientHeight; setScrollDepth(scrollHeight > 0 ? (scrollTop / scrollHeight) * 100 : 0); };
    container.addEventListener('click', handleClick);
    container.addEventListener('mousemove', handleMouseMove);
    container.addEventListener('scroll', handleScroll);
    return () => { container.removeEventListener('click', handleClick); container.removeEventListener('mousemove', handleMouseMove); container.removeEventListener('scroll', handleScroll); if (moveTimer) clearTimeout(moveTimer); };
  }, [previewUrl, snapToHotspot, viewportToDoc]);

  useEffect(() => {
    const container = containerRef.current, iframe = iframeRef.current;
    if (!container || !previewUrl) return;
    const onScroll = (e) => {
      const vpW = iframe?.clientWidth || container.clientWidth || 800, vpH = iframe?.clientHeight || container.clientHeight || 500;
      accScrollYRef.current = Math.max(0, accScrollYRef.current + e.deltaY);
      const scrollY = accScrollYRef.current;
      try { iframe?.contentWindow?.scrollBy(0, e.deltaY); } catch { }
      iframeScrollRef.current = { x: 0, y: scrollY, viewportW: vpW, viewportH: vpH };
      reseedDots(scrollY, vpW, vpH);
    };
    container.addEventListener('wheel', onScroll, { passive: true, capture: true });
    return () => container.removeEventListener('wheel', onScroll, { capture: true });
  }, [previewUrl, reseedDots]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    syncCanvasSize();
    const allLive = liveEventsRef.current, allReal = realTimeEvents;
    let rawEvents = [];
    if (heatmapStyle === 'click') rawEvents = [...allReal.filter(e => e.type === 'click'), ...allLive.filter(e => e.type === 'click')];
    else if (heatmapStyle === 'move') rawEvents = [...allReal.filter(e => e.type === 'mousemove'), ...allLive.filter(e => e.type === 'mousemove')];
    else if (heatmapStyle === 'scroll') rawEvents = allLive.filter(e => e.type === 'scroll').map(e => ({ x: canvas.width / 2, y: (e.depth / 100) * canvas.height }));
    const canvasPoints = clusterEvents(rawEvents.filter(e => typeof e.x === 'number' && typeof e.y === 'number' && Number.isFinite(e.x) && Number.isFinite(e.y)).map(e => { const cp = docToCanvas(e.x, e.y); return { x: cp.x, y: cp.y }; }).filter(p => Number.isFinite(p.x) && Number.isFinite(p.y)));
    renderHeatmap(canvas, canvasPoints, { radius: 28, maxOpacity: 0.85 });

    if (heatmapStyle === 'click' && rageClicks.length > 0) {
      const ctx = canvas.getContext('2d');
      ctx.save();
      for (const rage of rageClicks) {
        const cp = docToCanvas(rage.x, rage.y);
        if (!Number.isFinite(cp.x) || !Number.isFinite(cp.y)) continue;
        const r = 28 + rage.count * 4;
        const grd = ctx.createRadialGradient(cp.x, cp.y, 0, cp.x, cp.y, r);
        grd.addColorStop(0, 'rgba(220,38,38,0.9)'); grd.addColorStop(1, 'rgba(220,38,38,0)');
        ctx.fillStyle = grd; ctx.beginPath(); ctx.arc(cp.x, cp.y, r, 0, Math.PI * 2); ctx.fill();
        ctx.font = 'bold 14px sans-serif'; ctx.fillStyle = '#fff'; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText('\u26a0\ufe0f', cp.x, cp.y);
      }
      ctx.restore();
    }

    if (heatmapStyle === 'scroll') {
      const ctx = canvas.getContext('2d');
      const { y: scrollY, viewportH } = iframeScrollRef.current;
      const foldDocY = (scrollY || 0) + (viewportH || canvas.height);
      const foldY = docToCanvas(0, foldDocY).y;
      ctx.save();
      ctx.strokeStyle = `${T.green}cc`; ctx.lineWidth = 2; ctx.setLineDash([10, 6]);
      ctx.beginPath(); ctx.moveTo(0, foldY); ctx.lineTo(canvas.width, foldY); ctx.stroke();
      ctx.setLineDash([]);
      ctx.font = '11px monospace'; ctx.fillStyle = T.green;
      ctx.fillText(`↑ fold (${Math.round(scrollDepth)}%)`, 8, foldY - 7);
      ctx.restore();
    }
  }, [liveEventsTick, iframeScrollY, realTimeEvents, heatmapStyle, scrollDepth, rageClicks, docToCanvas, syncCanvasSize]);

  const resetHeatmap = useCallback(() => {
    setIframeHeight(2000); iframeHeightRef.current = 2000;
    setHasClicked(false);
    if (demoTimerRef.current) { clearInterval(demoTimerRef.current); demoTimerRef.current = null; }
    liveEventsRef.current = [];
    setRageClicks([]);
    accScrollYRef.current = 0;
    iframeScrollRef.current = { x: 0, y: 0, viewportW: 0, viewportH: 0 };
    setIframeScrollY(0); setScrollDepth(0);
    const canvas = canvasRef.current;
    if (canvas) canvas.getContext('2d').clearRect(0, 0, canvas.width, canvas.height);
    setLiveEventsTick(t => t + 1);
  }, []);

  useEffect(() => () => { if (demoTimerRef.current) clearInterval(demoTimerRef.current); }, []);

  const fetchTrackedWebsites = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/my/websites`, { headers: { 'Content-Type': 'application/json', ...authHeaders() } });
      const data = await res.json();
      const list = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      setTrackedWebsites(list); return list;
    } catch { setTrackedWebsites([]); return []; }
  }, [apiBase]);

  const getPageType = (path) => {
    if (path === '/' || path === '/home') return 'main';
    if (/\/(blog|news|article)/.test(path)) return 'content';
    if (/\/(about|contact|service)/.test(path)) return 'main';
    if (/\/(support|help|faq)/.test(path)) return 'support';
    if (/\/(login|register|auth)/.test(path)) return 'auth';
    if (/\/(privacy|terms|legal)/.test(path)) return 'legal';
    if (/\/(api|docs|documentation)/.test(path)) return 'technical';
    return 'content';
  };

  const fetchRealInteractionData = useCallback(async (websiteId, page = '/', baseUrlForPages = '') => {
    const reqId = ++latestReqRef.current;
    setLoadingRealData(true);
    try {
      const [eventsRes, pagesRes] = await Promise.all([
        fetch(`${apiBase}/my/analytics/events?website_id=${websiteId}&page=${encodeURIComponent(page)}&limit=500`, { headers: { 'Content-Type': 'application/json', ...authHeaders() } }),
        fetch(`${apiBase}/my/analytics/pages?website_id=${websiteId}`, { headers: { 'Content-Type': 'application/json', ...authHeaders() } }),
      ]);
      const [eventsData, pagesData] = await Promise.all([eventsRes.json().catch(() => ({ items: [] })), pagesRes.json().catch(() => ({ items: [] }))]);
      if (reqId !== latestReqRef.current) return;
      setRealTimeEvents((Array.isArray(eventsData?.items) ? eventsData.items : []).filter(e => typeof e.x === 'number' && typeof e.y === 'number').map(e => ({ type: e.type, x: e.x, y: e.y, timestamp: e.timestamp || e.ts || Date.now(), page: e.page || '/' })));
      const pages = Array.isArray(pagesData?.items) ? pagesData.items : [];
      const baseUrl = baseUrlForPages || selectedWebsite?.url || '';
      if (pages.length > 0) {
        setSitePages(pages.map(p => ({ path: p.page || p.path || '/', title: p.title || p.page || 'Untitled', fullUrl: `${baseUrl}${p.page || p.path || '/'}`, type: getPageType(p.page || p.path || '/'), status: 'active', views: p.views || p.pageviews || 0, clicks: p.clicks || 0 })));
      } else if (baseUrl) {
        try {
          const origin = new URL(baseUrl).origin;
          setSitePages(['/', '/about', '/contact', '/services', '/products', '/pricing', '/blog', '/news', '/support', '/faq', '/privacy', '/terms'].map(path => ({ path, title: path.replace('/', '') || 'Home', fullUrl: `${origin}${path}`, type: getPageType(path), status: 'inactive', views: 0, clicks: 0 })));
        } catch { setSitePages([]); }
      } else { setSitePages([]); }
    } catch { message.error('Failed to load analytics data'); setRealTimeEvents([]); setSitePages([]); }
    finally { setLoadingRealData(false); }
  }, [apiBase, selectedWebsite]);

  const refreshCurrentWebsiteData = useCallback(async () => {
    if (!selectedWebsite) { message.warning('Select a website first.'); return; }
    let pagePath = '/';
    if (previewUrl) { try { const base = new URL(selectedWebsite.url); pagePath = new URL(previewUrl, base.origin).pathname; } catch { pagePath = '/'; } }
    await fetchRealInteractionData(selectedWebsite.id, pagePath, selectedWebsite.url);
  }, [selectedWebsite, previewUrl, fetchRealInteractionData]);

  useEffect(() => { fetchTrackedWebsites(); }, [fetchTrackedWebsites]);
  useEffect(() => {
    if (!selectedWebsite || !previewUrl) return;
    const id = setInterval(refreshCurrentWebsiteData, 30_000);
    return () => clearInterval(id);
  }, [selectedWebsite, previewUrl, refreshCurrentWebsiteData]);

  const handleTrackedWebsiteSelect = useCallback(async (website) => {
    resetHeatmap(); setSelectedWebsite(website); setPreviewUrl(website.url); setLoading(true); setSitePages([]);
    await fetchRealInteractionData(website.id, '/', website.url);
    setLoading(false);
  }, [fetchRealInteractionData, resetHeatmap]);

  const handleClearPreview = () => { resetHeatmap(); setPreviewUrl(''); setSitePages([]); };
  const handlePageClick = (page) => { resetHeatmap(); setPreviewUrl(page.fullUrl); if (selectedWebsite) fetchRealInteractionData(selectedWebsite.id, page.path, selectedWebsite.url); };

  useImperativeHandle(ref, () => ({ loadWebsite: (w) => handleTrackedWebsiteSelect(w), refresh: () => refreshCurrentWebsiteData() }), [handleTrackedWebsiteSelect, refreshCurrentWebsiteData]);

  useEffect(() => { if (typeof onStatusChange === 'function') onStatusChange({ canRefresh: Boolean(selectedWebsite), isRefreshing: loadingRealData }); }, [onStatusChange, selectedWebsite, loadingRealData]);
  useEffect(() => { collectInteractiveHotspots(); }, [collectInteractiveHotspots, previewUrl, realTimeEvents]);
  useEffect(() => { const h = () => collectInteractiveHotspots(); window.addEventListener('resize', h); return () => window.removeEventListener('resize', h); }, [collectInteractiveHotspots]);

  const getPageTypeColor = (type) => ({ main: T.teal, content: T.green, support: T.purple, legal: T.txtMuted, auth: T.rose, technical: T.amber }[type] || T.txtMuted);
  const liveClicks = realTimeEvents.filter(e => e.type === 'click').length;
  const liveMoves  = realTimeEvents.filter(e => e.type === 'mousemove').length;
  const realClicks = realTimeEvents.filter(e => e.type === 'click').length;
  const totalClicks = realClicks;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      {/* ── Toolbar ── */}
      <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 14, padding: '16px 20px' }}>
        {/* Website chips */}
        {trackedWebsites.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, color: T.txtMuted, fontFamily: T.mono, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 8 }}>Tracked Sites</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {trackedWebsites.map(w => {
                const active = selectedWebsite?.id === w.id;
                return (
                  <button key={w.id} onClick={() => handleTrackedWebsiteSelect(w)} style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '5px 13px',
                    background: active ? `${T.teal}20` : 'transparent',
                    border: `1px solid ${active ? T.teal : T.border}`,
                    borderRadius: 99, cursor: 'pointer', transition: 'all 0.18s',
                    color: active ? T.teal : T.txtSec, fontSize: 12, fontFamily: T.mono,
                    boxShadow: active ? `0 0 12px ${T.tealGlow}` : 'none',
                  }}>
                    <GlobalOutlined style={{ fontSize: 10 }} />
                    {w.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Controls row */}
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <Select
            placeholder="Select a website…"
            style={{ flex: 1, minWidth: 220 }}
            value={selectedWebsite?.id ?? undefined}
            options={trackedWebsites.map(w => ({ label: w.url.replace(/^https?:\/\//, '').replace(/\/$/, ''), value: w.id }))}
            onChange={id => { const w = trackedWebsites.find(w => w.id === id); if (w) handleTrackedWebsiteSelect(w); }}
            showSearch optionFilterProp="label"
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={handleClearPreview} disabled={!previewUrl} style={{ padding: '6px 14px', background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 8, color: T.txtSec, cursor: previewUrl ? 'pointer' : 'not-allowed', fontSize: 12, fontFamily: T.sans, opacity: previewUrl ? 1 : 0.4, transition: 'all 0.15s' }}>
              Clear
            </button>
            <button disabled={!previewUrl} onClick={() => {
              if (!previewUrl) return;
              collectInteractiveHotspots();
              const ifr = iframeRef.current;
              reseedDots(0, ifr?.clientWidth || 800, ifr?.clientHeight || 500);
              setHasClicked(true);
            }} style={{
              display: 'flex', alignItems: 'center', gap: 7, padding: '6px 16px',
              background: hasClicked ? `${T.green}20` : `${T.teal}20`,
              border: `1px solid ${hasClicked ? T.green : T.teal}`,
              borderRadius: 8, cursor: previewUrl ? 'pointer' : 'not-allowed',
              color: hasClicked ? T.green : T.teal, fontSize: 12, fontFamily: T.sans,
              opacity: previewUrl ? 1 : 0.4, transition: 'all 0.2s',
              boxShadow: hasClicked ? `0 0 12px ${T.green}30` : `0 0 12px ${T.tealGlow}`,
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: hasClicked ? T.green : T.teal, animation: hasClicked ? 'pulse 1.5s infinite' : 'none' }} />
              {hasClicked ? 'Tracking Live' : 'Start Tracking'}
            </button>
          </div>
        </div>

        {/* Mode + status row */}
        {previewUrl && (
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 14, flexWrap: 'wrap', gap: 10 }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <ModeBtn active={heatmapStyle==='click'}  icon="🖱️" label="Clicks"    value="click"  onChange={setHeatmapStyle} />
              <ModeBtn active={heatmapStyle==='move'}   icon="👆" label="Movement" value="move"   onChange={setHeatmapStyle} />
              <ModeBtn active={heatmapStyle==='scroll'} icon="📜" label="Scroll"   value="scroll" onChange={setHeatmapStyle} />
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <div style={{ fontSize: 11, fontFamily: T.mono, color: loadingRealData ? T.amber : selectedWebsite ? T.green : T.txtMuted, display: 'flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: loadingRealData ? T.amber : selectedWebsite ? T.green : T.txtMuted, boxShadow: selectedWebsite ? `0 0 5px ${T.green}` : 'none' }} />
                {loadingRealData ? 'Loading data…' : selectedWebsite ? 'Live data connected' : 'No snippet detected'}
              </div>
              <button onClick={() => setShowPagesList(v => !v)} style={{ display: 'flex', alignItems: 'center', gap: 5, padding: '5px 12px', background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 7, color: T.txtSec, cursor: 'pointer', fontSize: 11, fontFamily: T.sans, transition: 'all 0.15s' }}>
                {showPagesList ? <EyeInvisibleOutlined /> : <EyeOutlined />}
                {showPagesList ? 'Hide Pages' : 'Pages'}
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Stats pills ── */}
      {previewUrl && (
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <StatPill color={T.rose}   label="CLICKS"    value={liveClicks} />
          <StatPill color={T.teal}   label="MOVES"     value={liveMoves} />
          <StatPill color={T.amber}  label="RECORDED"  value={realClicks} />
          <StatPill color={T.green}  label="SCROLL"    value={`${Math.round(scrollDepth)}%`} />
          <StatPill color={T.purple} label="TOTAL"     value={totalClicks} />
        </div>
      )}

      {/* ── Main preview area ── */}
      {previewUrl ? (
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
          {/* Preview */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div ref={containerRef} style={{
              border: `1px solid ${T.border}`,
              borderRadius: 12, overflow: 'visible', height: iframeHeight,
              position: 'relative', background: T.bgPanel,
              boxShadow: `0 0 0 1px ${T.border}, 0 8px 40px rgba(0,0,0,0.5)`,
            }}>
              {loading && (
                <div style={{ position: 'absolute', inset: 0, background: 'rgba(255,255,255,0.9)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', zIndex: 3, borderRadius: 12 }}>
                  <div style={{ width: 36, height: 36, border: `2px solid ${T.border}`, borderTopColor: T.teal, borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginBottom: 12 }} />
                  <span style={{ color: T.txtSec, fontSize: 12, fontFamily: T.mono }}>Loading website…</span>
                </div>
              )}

              {/* Heat legend */}
              {hasClicked && (
                <div style={{ position: 'absolute', top: 12, right: 12, zIndex: 5, pointerEvents: 'none', background: 'rgba(255,255,255,0.95)', border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 12px', backdropFilter: 'blur(8px)' }}>
                  <div style={{ fontSize: 9, fontFamily: T.mono, color: T.txtMuted, letterSpacing: '0.08em', marginBottom: 5 }}>INTENSITY</div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: 10, color: T.txtSec, fontFamily: T.mono }}>Cold</span>
                    <div style={{ width: 90, height: 7, background: 'linear-gradient(90deg,#3b82f6 0%,#06b6d4 25%,#22c55e 45%,#fbbf24 65%,#f97316 80%,#ef4444 100%)', borderRadius: 3 }} />
                    <span style={{ fontSize: 10, color: T.txtSec, fontFamily: T.mono }}>Hot</span>
                  </div>
                </div>
              )}

              {/* Click counter */}
              {hasClicked && (
                <div style={{ position: 'absolute', bottom: 12, right: 12, zIndex: 5, pointerEvents: 'none', background: 'rgba(255,255,255,0.95)', border: `1px solid ${T.border}`, borderRadius: 8, padding: '6px 12px', backdropFilter: 'blur(8px)', fontFamily: T.mono, fontSize: 11, color: T.txtSec }}>
                  <span style={{ color: T.teal, fontWeight: 700 }}>{totalClicks}</span> clicks recorded
                </div>
              )}

              {/* Pre-click hint */}
              {!hasClicked && (
                <div style={{ position: 'absolute', bottom: 14, left: '50%', transform: 'translateX(-50%)', zIndex: 5, pointerEvents: 'none', background: 'rgba(255,255,255,0.95)', border: `1px solid ${T.teal}40`, color: T.teal, fontSize: 11, padding: '7px 18px', borderRadius: 99, whiteSpace: 'nowrap', fontFamily: T.mono, boxShadow: `0 0 20px ${T.tealGlow}` }}>
                  🖱 Click anywhere to start recording
                </div>
              )}

              <iframe ref={iframeRef} src={previewUrl} style={{ width: '100%', height: iframeHeight, border: 'none', display: 'block', borderRadius: 12 }} title="Website Preview"
                onLoad={() => {
                  setLoading(false); collectInteractiveHotspots();
                  const ifr = iframeRef.current;
                  reseedDots(0, ifr?.clientWidth || 800, ifr?.clientHeight || 500);
                  try { const iDoc = iframeRef.current?.contentWindow?.document; if (iDoc) { const h = Math.max(iDoc.body?.scrollHeight || 0, iDoc.documentElement?.scrollHeight || 0, 2000); setIframeHeight(h); iframeHeightRef.current = h; } } catch { setIframeHeight(4000); iframeHeightRef.current = 4000; }
                }}
                onError={() => { setLoading(false); message.error('Failed to load — the site may block embedding.'); }}
              />

              {hasClicked && (
                <div ref={overlayRef} onWheel={(e) => {
                  const iframe = iframeRef.current, vpW = iframe?.clientWidth || 800, vpH = iframe?.clientHeight || 500;
                  accScrollYRef.current = Math.max(0, accScrollYRef.current + e.deltaY);
                  const scrollY = accScrollYRef.current;
                  try { iframe?.contentWindow?.scrollBy(0, e.deltaY); } catch { }
                  iframeScrollRef.current = { x: 0, y: scrollY, viewportW: vpW, viewportH: vpH };
                  reseedDots(scrollY, vpW, vpH);
                }} style={{ position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'all', background: 'transparent', cursor: 'crosshair' }} />
              )}

              {hasClicked && (
                <canvas ref={canvasRef} style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', pointerEvents: 'none', zIndex: 4 }} />
              )}
            </div>
          </div>

          {/* Pages sidebar */}
          {showPagesList && (
            <div style={{ width: 220, flexShrink: 0, background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, height: Math.min(iframeHeight, 680), overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
              <div style={{ padding: '12px 14px', borderBottom: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 11, fontFamily: T.mono, color: T.txtSec, letterSpacing: '0.06em' }}>PAGES</span>
                <span style={{ fontSize: 10, fontFamily: T.mono, color: T.txtMuted, background: `${T.teal}14`, border: `1px solid ${T.teal}30`, borderRadius: 4, padding: '1px 6px' }}>{sitePages.length}</span>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '6px 8px' }}>
                {sitePages.length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 12px', color: T.txtMuted, fontSize: 12 }}>No pages found</div>
                ) : (
                  sitePages.map((page, i) => (
                    <PageItem key={i} page={page} active={previewUrl === page.fullUrl} onClick={() => handlePageClick(page)} getColor={getPageTypeColor} />
                  ))
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Empty state */
        <div style={{ background: T.bgCard, border: `1px dashed ${T.border}`, borderRadius: 14, padding: '60px 40px', textAlign: 'center' }}>
          <div style={{ width: 56, height: 56, borderRadius: 14, background: `${T.teal}10`, border: `1px solid ${T.teal}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px', fontSize: 22 }}>
            🌐
          </div>
          <div style={{ fontSize: 16, fontWeight: 600, color: T.txtPri, marginBottom: 8 }}>Select a website to begin</div>
          <div style={{ fontSize: 12, color: T.txtSec, maxWidth: 360, margin: '0 auto', lineHeight: 1.7 }}>
            Choose one of your tracked websites above to load it in the preview and start capturing click, movement, and scroll heatmaps.
          </div>
        </div>
      )}

      {/* CSS animations */}
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%,100% { opacity: 1; box-shadow: 0 0 5px currentColor; } 50% { opacity: 0.5; box-shadow: 0 0 12px currentColor; } }
        ::-webkit-scrollbar { width: 5px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${T.border}; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: ${T.teal}; }
      `}</style>
    </div>
  );
});

// ─── HEATMAP PAGE ─────────────────────────────────────────────────────────────
const HeatMap = () => {
  const previewRef = useRef(null);
  const [currentPlan, setCurrentPlan] = useState(null);
  const [upgradeModalVisible, setUpgradeModalVisible] = useState(false);
  const apiBase = API_BASE;

  const fetchCurrentPlan = useCallback(async () => {
    try {
      const res = await fetch(`${apiBase}/my/current-plan`, { headers: { 'Content-Type': 'application/json', ...authHeaders() } });
      setCurrentPlan(res.ok ? await res.json() : null);
    } catch { setCurrentPlan(null); }
  }, [apiBase]);

  useEffect(() => { fetchCurrentPlan(); }, [fetchCurrentPlan]);

  const hasHeatmapAccess = hasFeatureAccess(currentPlan, 'heatmaps');
  const upgradeMessage   = getUpgradeMessage('heatmaps');

  if (!hasHeatmapAccess && currentPlan) {
    return (
      <div className="abo-content" style={{ background: T.bg, minHeight: '100vh' }}>
        <div style={{ marginBottom: 28 }}>
          <h1 style={{ fontFamily: T.sans, fontWeight: 400, fontSize: 26, color: T.txtPri, margin: 0, letterSpacing: '-0.5px' }}>Website Heatmap</h1>
          <p style={{ color: T.txtSec, marginTop: 6, fontSize: 13 }}>Visualize user interactions and behaviour with advanced heatmaps</p>
        </div>
        <div style={{ background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 16, padding: 40, textAlign: 'center' }}>
          <Result
            icon={<CrownOutlined style={{ color: T.purple }} />}
            title={<span style={{ color: T.txtPri }}>{upgradeMessage.title}</span>}
            subTitle={<span style={{ color: T.txtSec }}>{upgradeMessage.description}</span>}
            extra={[
              <Button key="upgrade" type="primary" size="large" onClick={() => setUpgradeModalVisible(true)} style={{ background: T.purple, borderColor: T.purple, fontFamily: T.sans }}>
                Upgrade to Enterprise
              </Button>,
            ]}
          />
        </div>
        <UpgradeModal visible={upgradeModalVisible} onClose={() => setUpgradeModalVisible(false)} limitType="heatmaps" currentPlan={currentPlan?.plan_name} />
      </div>
    );
  }

  return (
    <div className="abo-content" style={{ background: T.bg, minHeight: '100vh' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <div style={{ width: 36, height: 36, borderRadius: 10, background: `${T.teal}18`, border: `1px solid ${T.teal}30`, display: 'grid', placeItems: 'center', fontSize: 16 }}>
              🔥
            </div>
            <h1 style={{ fontFamily: T.sans, fontWeight: 570, fontSize: 26, color: T.txtPri, margin: 0, letterSpacing: '-0.5px' }}>
              Website Heatmap
            </h1>
          </div>
          <p style={{ color: T.txtSec, margin: 0, fontSize: 13, paddingLeft: 46 }}>
            Real-time click, movement & scroll depth analytics
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button onClick={() => previewRef.current?.refresh()} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 16px', background: 'transparent', border: `1px solid ${T.border}`, borderRadius: 9, color: T.txtSec, cursor: 'pointer', fontSize: 12, fontFamily: T.sans, transition: 'all 0.15s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = T.teal + '60'; e.currentTarget.style.color = T.teal; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = T.border; e.currentTarget.style.color = T.txtSec; }}>
            <ReloadOutlined /> Refresh Data
          </button>
        </div>
      </div>

      {/* Preview */}
      <WebsitePreview ref={previewRef} />

      {/* Info footer */}
      <div style={{ marginTop: 20, background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 14, padding: '18px 22px' }}>
        <div style={{ fontSize: 10, fontFamily: T.mono, color: T.txtMuted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: 14 }}>How it works</div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 14 }}>
          {[
            { icon: '🖱️', title: 'Click Map',     desc: 'Gaussian-blur density map of where users click — advanced two-pass rendering.' },
            { icon: '👆', title: 'Move Map',      desc: 'Mouse-movement density showing attention zones and navigation patterns.' },
            { icon: '📜', title: 'Scroll Depth',  desc: 'Visualises how far users scroll with a live fold line marker.' },
            { icon: '⚠️', title: 'Rage Clicks',   desc: 'Automatically detected frustration patterns highlighted with warning markers.' },
          ].map((item, i) => (
            <div key={i} style={{ display: 'flex', gap: 12, padding: '12px 14px', background: T.bgPanel, borderRadius: 10, border: `1px solid ${T.border}` }}>
              <span style={{ fontSize: 20, flexShrink: 0 }}>{item.icon}</span>
              <div>
                <div style={{ fontWeight: 600, fontSize: 12, color: T.txtPri, marginBottom: 4 }}>{item.title}</div>
                <div style={{ fontSize: 11, color: T.txtSec, lineHeight: 1.6 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={{ marginTop: 14, padding: '10px 14px', background: `${T.amber}08`, border: `1px solid ${T.amber}20`, borderRadius: 8, fontSize: 11, color: T.txtSec, lineHeight: 1.6 }}>
          <span style={{ color: T.amber, fontWeight: 600 }}>Note:</span> Cross-origin restrictions may limit event capture on external sites. For best results, install your tracking snippet on the target website.
        </div>
      </div>

      <UpgradeModal visible={upgradeModalVisible} onClose={() => setUpgradeModalVisible(false)} limitType="heatmaps" currentPlan={currentPlan?.plan_name} />
    </div>
  );
};

export { WebsitePreview };
export default HeatMap;